// CAD Library import: turn an SVG drawing (Illustrator: File ▸ Save As ▸ SVG; Inkscape, CorelDRAW, Affinity, any CAD
// program that saves SVG) into Falcon's shapes. Runs in the browser so the drawing's own styles, classes and transforms
// are worked out exactly as a browser draws them. Filled shapes become panels (named from the drawing's layers or groups,
// else one panel per closed shape, named by where it sits) that take colourway colours; outlines and stitch lines keep their own colour. Text, pictures and
// symbols aren't shapes: they're counted and reported so the user can convert them to outlines first.
import type { CadArea, CadArt, CadShape } from "../../shared/cadlib.js";

export interface ImportResult { art: CadArt; areas: CadArea[]; skipped: string[]; shapeCount: number; slot?: string }

const SHAPES = "path,rect,circle,ellipse,line,polyline,polygon";
const HIDDEN_PARENTS = "defs,clipPath,mask,symbol,pattern,marker,linearGradient,radialGradient,filter,metadata,title,desc";
const n = (v: number) => String(Math.round(v * 1000) / 1000);

function toHex(css: string): string | null {
  const s = css.trim();
  if (!s || s === "none" || s === "transparent") return null;
  if (/^url\(/.test(s)) return "#d1d5db";   // gradients and patterns: a light grey the user can colour
  const m = /^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:[,\s/]+([\d.]+%?))?\s*\)$/.exec(s);
  if (m) {
    if (m[4] !== undefined && parseFloat(m[4]) === 0) return null;
    return `#${[m[1], m[2], m[3]].map((x) => Math.max(0, Math.min(255, Math.round(Number(x)))).toString(16).padStart(2, "0")).join("")}`;
  }
  if (/^#[0-9a-f]{6}$/i.test(s)) return s.toLowerCase();
  if (/^#[0-9a-f]{3}$/i.test(s)) return `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`.toLowerCase();
  return "#9aa0a6";
}
const num = (el: Element, a: string) => parseFloat(el.getAttribute(a) || "0") || 0;
/** Any shape as path data. */
function pathOf(el: Element): string {
  const tag = el.localName;
  if (tag === "path") return (el.getAttribute("d") || "").trim();
  if (tag === "rect") {
    const x = num(el, "x"), y = num(el, "y"), w = num(el, "width"), h = num(el, "height");
    if (w <= 0 || h <= 0) return "";
    let rx = num(el, "rx"), ry = num(el, "ry");
    if (!el.hasAttribute("ry")) ry = rx; if (!el.hasAttribute("rx")) rx = ry;
    rx = Math.min(rx, w / 2); ry = Math.min(ry, h / 2);
    if (!rx || !ry) return `M${n(x)},${n(y)} H${n(x + w)} V${n(y + h)} H${n(x)} Z`;
    return `M${n(x + rx)},${n(y)} H${n(x + w - rx)} A${n(rx)},${n(ry)} 0 0 1 ${n(x + w)},${n(y + ry)} V${n(y + h - ry)} A${n(rx)},${n(ry)} 0 0 1 ${n(x + w - rx)},${n(y + h)} H${n(x + rx)} A${n(rx)},${n(ry)} 0 0 1 ${n(x)},${n(y + h - ry)} V${n(y + ry)} A${n(rx)},${n(ry)} 0 0 1 ${n(x + rx)},${n(y)} Z`;
  }
  if (tag === "circle" || tag === "ellipse") {
    const cx = num(el, "cx"), cy = num(el, "cy");
    const rx = tag === "circle" ? num(el, "r") : num(el, "rx"), ry = tag === "circle" ? rx : num(el, "ry");
    if (rx <= 0 || ry <= 0) return "";
    return `M${n(cx - rx)},${n(cy)} A${n(rx)},${n(ry)} 0 1 0 ${n(cx + rx)},${n(cy)} A${n(rx)},${n(ry)} 0 1 0 ${n(cx - rx)},${n(cy)} Z`;
  }
  if (tag === "line") return `M${n(num(el, "x1"))},${n(num(el, "y1"))} L${n(num(el, "x2"))},${n(num(el, "y2"))}`;
  const pts = (el.getAttribute("points") || "").trim().split(/[\s,]+/).map(Number).filter((x) => Number.isFinite(x));
  if (pts.length < 4) return "";
  let d = `M${n(pts[0])},${n(pts[1])}`;
  for (let i = 2; i + 1 < pts.length; i += 2) d += ` L${n(pts[i])},${n(pts[i + 1])}`;
  return tag === "polygon" ? `${d} Z` : d;
}
/** A layer or group name: Illustrator (id / data-name), Inkscape (inkscape:label) or a plain id. */
function nameOf(el: Element): string {
  const raw = el.getAttribute("data-name") || el.getAttributeNS("http://www.inkscape.org/namespaces/inkscape", "label") || el.getAttribute("inkscape:label") || el.getAttribute("id") || "";
  const t = raw.replace(/_x([0-9A-F]{2})_/gi, (_, h) => String.fromCharCode(parseInt(h, 16))).replace(/_/g, " ").replace(/\s+\d+$/, "").trim();
  // Program names that say nothing ("Layer 1", "path123", "g45", "XMLID 12") aren't area names.
  return /^(layer|ebene|calque|capa|livello|path|g|rect|circle|ellipse|polygon|polyline|line|group|xmlid|svg|artboard|clip ?path|object)\s*\d*$/i.test(t) || t.length > 60 ? "" : t;
}
const COLOUR_NAMES: Array<[string, number, number, number]> = [["white", 245, 245, 245], ["black", 20, 20, 20], ["grey", 150, 150, 150], ["light grey", 210, 210, 210], ["dark grey", 80, 80, 80], ["red", 200, 40, 40], ["orange", 240, 120, 30], ["yellow", 230, 220, 60], ["green", 50, 140, 70], ["blue", 40, 80, 170], ["navy", 30, 40, 80], ["brown", 110, 75, 45], ["beige", 220, 200, 160], ["pink", 230, 140, 170], ["purple", 120, 60, 150]];
function colourWord(hex: string) {
  const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
  return COLOUR_NAMES.reduce((best, c) => { const dist = (c[1] - r) ** 2 + (c[2] - g) ** 2 + (c[3] - b) ** 2; return dist < best[1] ? [c[0], dist] as [string, number] : best; }, ["", Infinity] as [string, number])[0];
}

/** A shape's box in the drawing's own units (its bounding box moved by its transform). */
function boxOf(el: SVGGraphicsElement, m: DOMMatrix | null): [number, number, number, number] {
  let b: DOMRect;
  try { b = el.getBBox(); } catch { return [0, 0, 0, 0]; }
  const pts = [[b.x, b.y], [b.x + b.width, b.y], [b.x, b.y + b.height], [b.x + b.width, b.y + b.height]].map(([x, y]) => (m ? new DOMPoint(x, y).matrixTransform(m) : new DOMPoint(x, y)));
  const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
  return [Math.min(...xs), Math.min(...ys), Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys)];
}
const lightness = (hex: string) => (0.299 * parseInt(hex.slice(1, 3), 16) + 0.587 * parseInt(hex.slice(3, 5), 16) + 0.114 * parseInt(hex.slice(5, 7), 16)) / 255;
const inside = (a: number[], b: number[]) => a[0] >= b[0] - 1 && a[1] >= b[1] - 1 && a[0] + a[2] <= b[0] + b[2] + 1 && a[1] + a[3] <= b[1] + b[3] + 1;

/**
 * Which filled shapes are panels. A layer or group the designer named stays one panel (all its shapes of one colour).
 * Every other closed shape is a panel of its own, named by where it sits on the garment (collar, yoke, sleeve, cuff…);
 * the names are a starting point the user can change. Drawings made the Illustrator way — one dark silhouette with
 * light panels laid on top, so the gaps read as lines — keep the silhouette and the small dark shapes as line work, and
 * tiny shapes (button holes, rivets) stay details, so neither can be picked by mistake.
 */
function planPanels(filled: Array<{ shape: CadShape; fill: string; group: string; bb: [number, number, number, number]; order: number }>, view: "front" | "back" | "side") {
  const area = (b: number[]) => Math.max(0, b[2]) * Math.max(0, b[3]);
  const gx = Math.min(...filled.map((f) => f.bb[0])), gy = Math.min(...filled.map((f) => f.bb[1]));
  const G = [gx, gy, Math.max(...filled.map((f) => f.bb[0] + f.bb[2])) - gx, Math.max(...filled.map((f) => f.bb[1] + f.bb[3])) - gy];
  const total = area(G) || 1;
  const underlay = new Set<typeof filled[number]>();
  for (const f of filled) {
    if (f.group || lightness(f.fill) > 0.25 || area(f.bb) < total * 0.25) continue;
    const onTop = filled.filter((o) => o.order > f.order && lightness(o.fill) > 0.5 && inside(o.bb, f.bb) && area(o.bb) > total * 0.004).length;
    if (onTop >= 2) underlay.add(f);
  }
  const lineArt = underlay.size > 0;
  const named = new Map<string, { name: string; fill: string; shapes: CadShape[] }>();
  const out: Array<{ name: string; fill: string; shapes: CadShape[] }> = [];
  const loose: Array<{ f: typeof filled[number]; name: string }> = [];
  for (const f of filled) {
    if (underlay.has(f)) continue;
    if (f.group) {
      const key = `${f.group}|${f.fill}`;
      let g = named.get(key);
      if (!g) {
        const sameGroup = Array.from(named.values()).some((x) => x.name === f.group);
        g = { name: sameGroup ? `${f.group} (${colourWord(f.fill)})` : f.group, fill: f.fill, shapes: [] };
        named.set(key, g); out.push(g);
      }
      g.shapes.push(f.shape);
      continue;
    }
    const a = area(f.bb) / total;
    if (a < 0.001) continue;                                     // button holes, rivets, stitch dots
    if (lineArt && lightness(f.fill) < 0.25 && a < 0.05) continue; // fold lines and seams drawn as thin dark shapes
    loose.push({ f, name: placeName(f.bb, G, view) });
  }
  // Same name twice (two "Panel"s): number them.
  const seen = new Map<string, number>();
  for (const l of loose) seen.set(l.name, (seen.get(l.name) || 0) + 1);
  // Very busy drawings: the biggest pieces are panels, the smallest stay part of the drawing.
  if (loose.length + out.length > 100) { loose.sort((a, b) => area(b.f.bb) - area(a.f.bb)); loose.length = Math.max(0, 100 - out.length); loose.sort((a, b) => a.f.order - b.f.order); }
  const count = new Map<string, number>();
  for (const l of loose) {
    let name = l.name;
    if ((seen.get(name) || 0) > 1) { const k = (count.get(name) || 0) + 1; count.set(name, k); name = `${name} ${k}`; }
    out.push({ name, fill: l.f.fill, shapes: [l.f.shape] });
  }
  return out;
}
/** A panel's name from where it sits on the garment (left and right as you look at the drawing). */
export function placeName(b: number[], G: number[], view: "front" | "back" | "side"): string {
  const u = (b[0] + b[2] / 2 - G[0]) / (G[2] || 1), v = (b[1] + b[3] / 2 - G[1]) / (G[3] || 1);
  const w = b[2] / (G[2] || 1), h = b[3] / (G[3] || 1);
  const side = view === "side" ? "" : u < 0.47 ? "Left" : u > 0.53 ? "Right" : "";
  const named = (what: string) => (side ? `${side} ${what}` : what.charAt(0).toUpperCase() + what.slice(1));
  const centre = Math.abs(u - 0.5) < 0.06;
  if (h > 0.4 && w < 0.1 && centre) return view === "back" ? "Centre back" : "Placket";
  if (h > 0.35 && (u < 0.25 || u > 0.75)) return named("sleeve");
  if (v > 0.75 && (u < 0.25 || u > 0.75) && w < 0.25) return named("cuff");
  if (h > 0.4) return w > 0.5 && centre ? (view === "back" ? "Back" : "Body") : named(view === "back" ? "back" : "front");
  if (v < 0.25 && Math.abs(u - 0.5) < 0.15 && w > 0.12 && h < 0.07) return v < 0.03 ? "Collar band" : "Collar stand";
  if (v < 0.25 && Math.abs(u - 0.5) < 0.15 && side && h < 0.06) return named("neckband");
  if (v < 0.25 && Math.abs(u - 0.5) < 0.15) return side ? named("collar") : view === "back" ? "Collar" : "Inside neck";
  if (v < 0.22) return named(view === "back" ? "yoke" : "shoulder");
  if (v > 0.85 && w > 0.3) return "Hem";
  if (w < 0.3 && h < 0.3 && v > 0.2 && v < 0.75) return named("pocket");
  return "Panel";
}

/** Remove anything that isn't drawing: scripts, embedded pages, event handlers, links to other sites. */
function sanitize(root: Element) {
  root.querySelectorAll("script,foreignObject,iframe,object,embed,audio,video,a,image,use").forEach((e) => {
    if (e.localName === "a") e.replaceWith(...Array.from(e.childNodes)); else e.remove();
  });
  for (const e of [root, ...Array.from(root.querySelectorAll("*"))]) {
    for (const at of Array.from(e.attributes)) {
      if (/^on/i.test(at.name) || (/href$/i.test(at.name) && !/^#/.test(at.value))) e.removeAttribute(at.name);
    }
  }
  root.querySelectorAll("style").forEach((s) => { s.textContent = (s.textContent || "").replace(/@import[^;]*;?/gi, "").replace(/url\((?!\s*['"]?#)[^)]*\)/gi, "none"); });
}

/**
 * Convert an SVG file's text. `trim` mode: no areas; the colour most of the trim is drawn in becomes the trim's colour
 * slot ("$"), so a placed trim takes the colourway colour. `prefix` keeps area ids unique when importing more views.
 */
export function importSvg(text: string, opts: { trim?: boolean; prefix?: string; existing?: CadArea[]; view?: "front" | "back" | "side" } = {}): ImportResult {
  if (/<!ENTITY/i.test(text)) throw new Error("This SVG uses entities, which Falcon doesn't read. Save it again as plain SVG (Illustrator: SVG 1.1, Styling: Presentation Attributes).");
  const doc = new DOMParser().parseFromString(text, "image/svg+xml");
  const src = doc.documentElement;
  if (!src || src.localName !== "svg" || doc.querySelector("parsererror")) throw new Error("This isn't an SVG file Falcon can read. In Illustrator use File ▸ Save As ▸ SVG (or Export ▸ Export As ▸ SVG).");
  sanitize(src);
  const skipped: string[] = [];
  const count = (sel: string, what: string) => { const k = src.querySelectorAll(sel).length; if (k) skipped.push(`${k} ${what}`); };
  count("text", "text item(s): convert text to outlines first (Illustrator: Type ▸ Create Outlines)");
  // The drawing is laid out off-screen so the browser works out styles and transforms exactly.
  const host = document.createElement("div");
  host.style.cssText = "position:fixed;left:-20000px;top:0;width:1000px;height:1000px;overflow:hidden;opacity:0;pointer-events:none";
  const svg = document.importNode(src, true) as unknown as SVGSVGElement;
  host.appendChild(svg);
  document.body.appendChild(host);
  try {
    if (!svg.getAttribute("width")) svg.setAttribute("width", "1000");
    if (!svg.getAttribute("height")) svg.setAttribute("height", "1000");
    const rootInv = svg.getScreenCTM()?.inverse();
    if (!rootInv) throw new Error("The drawing couldn't be read.");
    const els = Array.from(svg.querySelectorAll<SVGGraphicsElement>(SHAPES)).filter((e) => !e.closest(HIDDEN_PARENTS));
    const shapes: CadShape[] = [];
    const areas: CadArea[] = [];
    const used = new Set((opts.existing || []).map((a) => a.id));
    // Filled shapes are worked out after the whole drawing is read: which are panels, which are line work and details.
    const filled: Array<{ shape: CadShape; fill: string; group: string; bb: [number, number, number, number]; order: number }> = [];
    let invisible = 0;
    for (const el of els) {
      const cs = getComputedStyle(el);
      if (cs.display === "none" || cs.visibility === "hidden" || parseFloat(cs.opacity) === 0) { invisible++; continue; }
      const d = pathOf(el);
      if (!d || !/^[MmLlHhVvCcSsQqTtAaZz0-9eE.,\s+-]+$/.test(d)) continue;
      const fill = el.localName === "line" || parseFloat(cs.fillOpacity) === 0 ? null : toHex(cs.fill);
      const stroke = parseFloat(cs.strokeOpacity) === 0 ? null : toHex(cs.stroke);
      if (!fill && !stroke) continue;
      const ctm = el.getScreenCTM();
      const m = ctm ? rootInv.multiply(ctm) : null;
      const mat = m ? [m.a, m.b, m.c, m.d, m.e, m.f].map((x) => Math.round(x * 100000) / 100000) : undefined;
      const sw = stroke ? parseFloat(cs.strokeWidth) || 1 : 0;
      const dashRaw = cs.strokeDasharray && cs.strokeDasharray !== "none" ? cs.strokeDasharray.replace(/px/g, "").replace(/\s*,\s*/g, " ").trim() : "";
      const shape: CadShape = { d, fill: fill || "none", stroke: stroke || "none", sw, ...(dashRaw && /^[0-9.\s]{1,40}$/.test(dashRaw) ? { dash: dashRaw } : {}), ...(mat ? { m: mat } : {}), ...(cs.fillRule === "evenodd" ? { eo: true } : {}) };
      if (fill && !opts.trim) {
        let group = nameOf(el);
        for (let p = el.parentElement; !group && p && p !== (svg as unknown as Element); p = p.parentElement) group = nameOf(p);
        filled.push({ shape, fill, group, bb: boxOf(el, m), order: shapes.length });
      }
      shapes.push(shape);
    }
    if (filled.length) {
      const plan = planPanels(filled, opts.view || "front");
      for (const g of plan) {
        let id = `${opts.prefix || "a"}${areas.length + 1}`;
        while (used.has(id)) id = `${id}x`;
        used.add(id);
        const word = colourWord(g.fill);
        areas.push({ id, name: g.name.slice(0, 80), role: "fixed", colour: { name: word.charAt(0).toUpperCase() + word.slice(1), hex: g.fill, pantone: "" }, fabric: "" });
        for (const sh of g.shapes) sh.area = id;
      }
    }
    if (invisible) skipped.push(`${invisible} hidden shape(s)`);
    if (!shapes.length) throw new Error("No shapes found in this drawing. Make sure the garment is drawn with shapes (paths), not a picture placed in the file.");
    // The box: the drawing's page (viewBox), else the shapes' own extent.
    const vb = svg.viewBox?.baseVal;
    let box: [number, number, number, number];
    const bb = svg.getBBox();
    if (vb && vb.width > 0 && vb.height > 0 && !opts.trim) box = [vb.x, vb.y, vb.width, vb.height];
    else if (bb.width > 0 && bb.height > 0) {
      const pad = Math.max(bb.width, bb.height) * 0.02;
      box = [bb.x - pad, bb.y - pad, bb.width + 2 * pad, bb.height + 2 * pad];
    } else box = [0, 0, 1000, 1000];
    let slot: string | undefined;
    if (opts.trim) {
      // The colour most of the trim is drawn in becomes its colour slot.
      const tally = new Map<string, number>();
      for (const s of shapes) if (s.fill !== "none") tally.set(s.fill, (tally.get(s.fill) || 0) + 1);
      const top = Array.from(tally.entries()).sort((a, b) => b[1] - a[1])[0]?.[0];
      if (top) { slot = top; for (const s of shapes) if (s.fill === top) s.fill = "$"; }
    }
    return { art: { box: box.map((x) => Math.round(x * 1000) / 1000) as [number, number, number, number], shapes }, areas, skipped, shapeCount: shapes.length, ...(slot ? { slot } : {}) };
  } finally {
    host.remove();
  }
}
